import { useState, type FormEvent } from "react";
import { useAuth } from "../hooks/useAuth";
import { submitDraft } from "../services/api";
import { Button } from "./Button";

interface DraftSectionProps {
  conversationId: string;
}

type StatusType = "processing" | "success" | "error";

const STATUS_STYLES: Record<StatusType, string> = {
  processing: "bg-blue-50 text-blue-700",
  success: "bg-green-50 text-green-700",
  error: "bg-red-50 text-red-700",
};

export function DraftSection({ conversationId }: DraftSectionProps) {
  const { token, refreshToken } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ message: string; type: StatusType } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!prompt.trim() || !token) return;

    setSubmitting(true);
    setStatus({ message: "Generating draft…", type: "processing" });

    try {
      const data = await submitDraft(conversationId, prompt.trim(), token, refreshToken);

      if (data.status === "processing") {
        setStatus({
          message: "Draft is being generated. It will appear in the conversation shortly.",
          type: "success",
        });
        setPrompt("");
      } else {
        throw new Error(data.error || "Unknown error");
      }
    } catch (error) {
      setStatus({
        message: error instanceof Error ? error.message : "Failed to generate draft",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-4 border-t border-(--border-color,#e5e7eb) pt-4">
      <h4 className="mb-3 text-sm font-semibold text-(--text-muted,#6b7280)">Draft Reply</h4>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <textarea
            rows={3}
            placeholder="e.g. Tell them set lists are Premium, suggest upgrading with a 14-day trial..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            readOnly={submitting}
            className="min-h-[60px] w-full resize-y rounded-md border border-(--border-color,#d1d5db) bg-(--input-background,#fff) px-3 py-2 font-[inherit] text-sm text-(--text-color,#111) placeholder:text-[0.8125rem] placeholder:text-(--text-muted,#9ca3af) focus:border-(--accent-color,#3b82f6) focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
          />
        </div>
        <Button type="submit" fullWidth className="mt-2" disabled={submitting}>
          Generate Draft
        </Button>
      </form>
      {status && (
        <div className={`mt-2.5 rounded-md px-3 py-2 text-center text-[0.8125rem] ${STATUS_STYLES[status.type]}`}>
          {status.message}
        </div>
      )}
    </div>
  );
}
